import React, { Fragment, useEffect, useState } from "react";
import { api } from "../api";
import ScheduleRules from "./ScheduleRules";
import VolumePrediction from "./VolumePrediction";

const STATUS_COLORS: Record<string, string> = {
  on_time: "#1e8e3e", delayed: "#f79227", delayed_ok: "#f79227", recovered: "#5b4dc4",
  failed: "#f54b3c", missed: "#9e9e9e", running: "#2f7bc4",
};

const TIMING_LABEL: Record<string, string> = { on_time: "On Time", delayed: "Delayed", missed: "Missed" };

const WEEK_DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

function fmtDateTime(iso?: string | null): string {
  if (!iso) return "-";
  return new Date(iso).toISOString().replace("T", " ").slice(0, 19);
}

function fmtHHMM(iso?: string | null): string {
  if (!iso) return "-";
  return new Date(iso).toISOString().substring(11, 16);
}

interface DetailPanelProps {
  assetName: string | null;
  onClose: () => void;
}

const DetailPanel: React.FC<DetailPanelProps> = ({ assetName, onClose }) => {
  const [data, setData] = useState<any>(null);
  const [error, setError] = useState("");
  const [expandedDays, setExpandedDays] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!assetName) return;
    setData(null);
    setError("");
    setExpandedDays(new Set());
    api.detail(assetName).then(({ data: d }) => {
      if (d && d.error) setError(d.error);
      else setData(d);
    });
  }, [assetName]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  if (!assetName) return null;

  // Normalize response: supports new shape { schedule, history, runs }
  // and current backend shape { asset, recent_runs } during transition
  const rawAsset = data && data.asset;
  const sched = (data && (data.schedule || (rawAsset ? {
    data_asset: rawAsset.asset_name,
    gdo:         rawAsset.gdo,
    market:      rawAsset.market,
    trigger_type: rawAsset.trigger_type,
  } : {}))) || {};
  const totals = (data && data.history && data.history.totals) || {};
  const days: any[] = (data && data.history && data.history.daily_history) || [];
  const weekly: Record<string, string> = sched.weekly_schedule || {};
  const runs: any[] = ((data && (data.runs || data.recent_runs)) || []).map((r: any) => ({
    ...r,
    JobStatus:          r.JobStatus          ?? r.job_status    ?? r.exec_status,
    StartTimestamp:     r.StartTimestamp      ?? r.start_timestamp,
    EndTimestamp:       r.EndTimestamp        ?? r.end_timestamp,
    TriggerType:        r.TriggerType         ?? r.trigger_type,
    MessageDescription: r.MessageDescription  ?? r.message_description,
  }));

  const scheduleFields: Array<[string, any]> = [
    ["GDO", sched.gdo], ["Market", sched.market], ["Trigger Type", sched.trigger_type],
    ["Critical", sched.critical], ["Cadence", sched.cadence],
    ["Avg Runs/Day", sched.avg_runs_per_day], ["Total Runs (observed)", sched.total_runs],
    ["Avg Runtime", sched.avg_runtime], ["Observed Days", sched.observed_days],
    ["Observed Times (UTC)", sched.observed_times], ["Gold Trigger Time", sched.gold_trigger_time],
    ["Gold Run Days", sched.gold_run_days], ["Silver vs Gold", sched.silver_vs_gold],
    ["Source Trigger", sched.source_trigger],
  ];

  const todayIdx = new Date().getUTCDay();
  const todayName = WEEK_DAYS[(todayIdx + 6) % 7]; // JS: 0=Sun, we need Mon=0

  function toggleDay(dateKey: string) {
    setExpandedDays((prev) => {
      const next = new Set(prev);
      if (next.has(dateKey)) next.delete(dateKey);
      else next.add(dateKey);
      return next;
    });
  }

  return (
    <div className="detail-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="detail-panel" onClick={(e) => e.stopPropagation()}>
        <div className="detail-header">
          <h2 id="detail-title">{assetName}</h2>
          <button className="detail-close" onClick={onClose}>&times;</button>
        </div>
        <div className="detail-body">
          {/* 1. Schedule Info */}
          <div className="detail-section">
            <h3>Schedule Info</h3>
            {error ? (
              <div className="detail-grid">
                <div className="detail-item"><span className="value" style={{ color: "#e74c3c" }}>{error}</span></div>
              </div>
            ) : !data ? (
              <div className="detail-grid">
                <div className="detail-item"><span className="value">Loading...</span></div>
              </div>
            ) : !sched.data_asset ? (
              <div className="detail-grid">
                <div className="detail-item"><span className="value">No schedule data found for this asset (no history and no CSV entry)</span></div>
              </div>
            ) : (
              <div className="detail-grid" id="detail-schedule">
                {scheduleFields.map(([label, value]) => (
                  <div className="detail-item" key={label}>
                    <div className="label">{label}</div>
                    <div className="value">{value || "-"}</div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* 2. Weekly Expected Times */}
          <div className="detail-section">
            <h3>Weekly Expected Times (UTC)</h3>
            <div className="weekly-grid" id="detail-weekly">
              {WEEK_DAYS.map((day) => {
                const times = weekly[day] || "";
                const isToday = day === todayName;
                const hasRun = times && times !== "No run" && times !== "No data";
                return (
                  <div className={`day-cell${isToday ? " today" : ""}`} key={day}>
                    <div className="day-name">{day.substring(0, 3)}</div>
                    <div className={`day-times${hasRun ? "" : " no-run"}`}>
                      {hasRun
                        ? times.split(";").map((t: string, i: number) => <div key={i}>{t}</div>)
                        : (times || "\u2014")}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* 3. Schedule Rules */}
          <ScheduleRules asset={assetName} />

          {/* 4. Run Prediction & Baseline */}
          <VolumePrediction asset={assetName} />

          {/* 5. 7-Day Performance */}
          <div className="detail-section">
            <h3>7-Day Performance</h3>
            {!totals || Object.keys(totals).length === 0 ? (
              <div className="metrics-bar" id="detail-metrics">
                <div className="metric-item"><span className="metric-value">No data</span></div>
              </div>
            ) : (
              <div className="metrics-bar" id="detail-metrics">
                <div className="metric-item"><div className="metric-value success-text">{totals.on_time_pct || 0}%</div><div className="metric-label">On-Time Rate</div></div>
                <div className="metric-item"><div className="metric-value">{totals.on_time || 0}</div><div className="metric-label">On Time</div></div>
                <div className="metric-item"><div className="metric-value recovered-text">{totals.recovered || 0}</div><div className="metric-label">Recovered</div></div>
                <div className="metric-item"><div className="metric-value delayed-text">{totals.delayed || 0}</div><div className="metric-label">Delayed</div></div>
                <div className="metric-item"><div className="metric-value failed-text">{totals.failed || 0}</div><div className="metric-label">Failed</div></div>
                <div className="metric-item"><div className="metric-value">{totals.total_slots || 0}</div><div className="metric-label">Total Slots</div></div>
              </div>
            )}
            {totals && Object.keys(totals).length > 0 && days.length > 0 && (
              <div className="daily-history" id="detail-daily-history">
                <table className="history-table">
                  <thead>
                    <tr>
                      <th>Date</th><th>Day</th><th>Slots</th><th>Status</th><th>Runs</th><th>Pass/Fail</th><th>Avg Duration</th>
                    </tr>
                  </thead>
                  <tbody>
                    {days.map((d: any) => {
                      const expanded = expandedDays.has(d.date);
                      const slotDetails: any[] = d.slot_details || [];
                      return (
                        <Fragment key={d.date}>
                          <tr className="hist-day-row" style={{ cursor: "pointer" }}
                              title="Click to expand/collapse slot details" onClick={() => toggleDay(d.date)}>
                            <td>{d.date}</td>
                            <td>{d.day}</td>
                            <td>{d.expected_slots}</td>
                            <td className="slot-dots">
                              {(d.slot_statuses || []).map((s: string, i: number) => (
                                <span key={i} className="slot-dot" style={{ background: STATUS_COLORS[s] || "#636e72" }} title={s}></span>
                              )) || "-"}
                            </td>
                            <td>{d.total_runs}</td>
                            <td>
                              <span className="pass-count">{d.passed}&#10003;</span>
                              {d.failed > 0 && <span className="fail-count">{d.failed}&#10007;</span>}
                              {d.had_recovery && <span className="recovery-badge">&#8635;</span>}
                            </td>
                            <td>
                              {d.avg_duration}{" "}
                              <span style={{ color: "#64748b", fontSize: ".7rem" }}>{slotDetails.length ? (expanded ? "\u25b2" : "\u25bc") : ""}</span>
                            </td>
                          </tr>
                          {expanded && slotDetails.map((slot: any, si: number) => (
                            <Fragment key={`${d.date}-slot-${si}`}>
                              <tr className="slot-detail-row">
                                <td colSpan={7} style={{ paddingLeft: 20, fontSize: "0.8rem", color: "#475569", borderTop: "none" }}>
                                  <span style={{ color: STATUS_COLORS[slot.status] || "#636e72" }}>&#9679;</span>{" "}
                                  Slot {si + 1} @ {slot.expected}
                                  {slot.hist_avg_end ? ` \u2192 avg end ${slot.hist_avg_end}` : ""}
                                  {" \u2014 "}<b>{slot.status}</b>{" "}
                                  ({(slot.runs || []).length} run{(slot.runs || []).length !== 1 ? "s" : ""})
                                </td>
                              </tr>
                              {(slot.runs || []).map((r: any, ri: number) => (
                                <tr className="slot-run-row" key={`${d.date}-slot-${si}-run-${ri}`}>
                                  <td colSpan={7} style={{ paddingLeft: 32, fontSize: "0.75rem", color: "#475569" }}>
                                    &nbsp;&nbsp;&#8627; {fmtHHMM(r.start)}&#8211;{fmtHHMM(r.end)}
                                    {r.duration_mins != null ? ` (${r.duration_mins}m)` : ""}
                                    {r.timing_status && <span className={`timing-badge timing-${r.timing_status}`}>{TIMING_LABEL[r.timing_status] || r.timing_status}</span>}
                                    {r.exec_status && <span className={`exec-status-mini exec-${r.exec_status}`}>{r.exec_status}</span>}
                                    {r.job_status && <span style={{ color: "#64748b", marginLeft: 4 }}>{r.job_status}</span>}
                                  </td>
                                </tr>
                              ))}
                            </Fragment>
                          ))}
                        </Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* 6. Recent Runs */}
          <div className="detail-section">
            <h3>Recent Runs (Last 7 Days)</h3>
            <div className="detail-runs-container">
              <table className="detail-runs-table">
                <thead>
                  <tr><th>Status</th><th>Start</th><th>End</th><th>Duration</th><th>Trigger</th><th>Message</th></tr>
                </thead>
                <tbody id="detail-runs">
                  {runs.length === 0 ? (
                    <tr><td colSpan={6} style={{ textAlign: "center", color: "#888" }}>No runs in last 7 days</td></tr>
                  ) : (
                    runs.map((r: any, i: number) => {
                      const status = (r.JobStatus || "").toLowerCase();
                      const statusClass = status === "passed" ? "run-passed" : status === "failed" ? "run-failed" : "run-running";
                      const durMins = r.StartTimestamp && r.EndTimestamp
                        ? Math.round((new Date(r.EndTimestamp).getTime() - new Date(r.StartTimestamp).getTime()) / 60000)
                        : null;
                      return (
                        <tr key={i}>
                          <td><span className={statusClass}>{r.JobStatus || "-"}</span></td>
                          <td>{fmtDateTime(r.StartTimestamp)}</td>
                          <td>{fmtDateTime(r.EndTimestamp)}</td>
                          <td>{durMins != null ? `${durMins} min` : "-"}</td>
                          <td>{r.TriggerType || "-"}</td>
                          <td title={r.MessageDescription || ""}>{(r.MessageDescription || "-").slice(0, 50)}</td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DetailPanel;
